import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Icon,
  alpha,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { UserRole, mockUserRoles } from "../../../mock.data";

type PermissionModule = keyof UserRole["permissions"];
type PermissionAction<T extends PermissionModule> =
  keyof UserRole["permissions"][T];

export const PermissionsOverview: React.FC = () => {
  const theme = useTheme();
  const [roles, setRoles] = useState<UserRole[]>(mockUserRoles);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    permissions: UserRole["permissions"];
  }>({
    name: "",
    permissions: {
      journals: {
        create: false,
        edit: false,
        delete: false,
        post: false,
        reverse: false,
        view: false,
      },
      vouchers: {
        create: false,
        edit: false,
        delete: false,
        post: false,
        reverse: false,
        view: false,
      },
      coa: {
        create: false,
        edit: false,
        delete: false,
        view: false,
      },
      ledger: {
        view: false,
        export: false,
      },
      configuration: {
        view: false,
        edit: false,
      },
    },
  });

  const permissionModules = [
    {
      key: "journals" as const,
      label: "Journals",
      icon: "book",
      color: "#8B5CF6",
      permissions: [
        "create",
        "edit",
        "delete",
        "post",
        "reverse",
        "view",
      ] as const,
    },
    {
      key: "vouchers" as const,
      label: "Vouchers",
      icon: "receipt",
      color: "#F59E0B",
      permissions: [
        "create",
        "edit",
        "delete",
        "post",
        "reverse",
        "view",
      ] as const,
    },
    {
      key: "coa" as const,
      label: "Chart of Accounts",
      icon: "account_tree",
      color: "#10B981",
      permissions: ["create", "edit", "delete", "view"] as const,
    },
    {
      key: "ledger" as const,
      label: "Ledger",
      icon: "table_rows",
      color: "#06B6D4",
      permissions: ["view", "export"] as const,
    },
    {
      key: "configuration" as const,
      label: "Configuration",
      icon: "settings",
      color: "#EF4444",
      permissions: ["view", "edit"] as const,
    },
  ];

  const handleOpenDialog = (role?: UserRole) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        permissions: { ...role.permissions },
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: "",
        permissions: {
          journals: {
            create: false,
            edit: false,
            delete: false,
            post: false,
            reverse: false,
            view: false,
          },
          vouchers: {
            create: false,
            edit: false,
            delete: false,
            post: false,
            reverse: false,
            view: false,
          },
          coa: {
            create: false,
            edit: false,
            delete: false,
            view: false,
          },
          ledger: {
            view: false,
            export: false,
          },
          configuration: {
            view: false,
            edit: false,
          },
        },
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingRole(null);
  };

  const handleSave = () => {
    const now = moment().valueOf();

    if (editingRole) {
      // Update existing role
      setRoles((prev) =>
        prev.map((role) =>
          role.id === editingRole.id
            ? { ...role, ...formData, updated_at: now }
            : role
        )
      );
    } else {
      // Create new role
      const newRole: UserRole = {
        id: `role-${Date.now()}`,
        ...formData,
        created_at: now,
        updated_at: now,
      };
      setRoles((prev) => [...prev, newRole]);
    }

    handleCloseDialog();
  };

  const handlePermissionToggle = <T extends PermissionModule>(
    module: T,
    permission: PermissionAction<T>,
    value: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [permission]: value,
        },
      },
    }));
  };

  const handleQuickToggle = <T extends PermissionModule>(
    roleId: string,
    module: T,
    permission: PermissionAction<T>
  ) => {
    setRoles((prev) =>
      prev.map((role) => {
        if (role.id === roleId) {
          const currentValue = role.permissions[module][permission];
          return {
            ...role,
            permissions: {
              ...role.permissions,
              [module]: {
                ...role.permissions[module],
                [permission]: !currentValue,
              },
            },
            updated_at: moment().valueOf(),
          };
        }
        return role;
      })
    );
  };

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <Icon sx={{ color: "success.main", fontSize: 18 }}>check_circle</Icon>
    ) : (
      <Icon sx={{ color: "error.main", fontSize: 18 }}>cancel</Icon>
    );
  };

  const getRolePermissionCount = (role: UserRole) => {
    let total = 0;
    let granted = 0;

    permissionModules.forEach((module) => {
      module.permissions.forEach((permission) => {
        total++;
        const modulePermissions = role.permissions[module.key];
        if (
          modulePermissions &&
          permission in modulePermissions &&
          modulePermissions[permission as keyof typeof modulePermissions]
        ) {
          granted++;
        }
      });
    });

    return { granted, total };
  };

  const hasPermission = <T extends PermissionModule>(
    role: UserRole,
    module: T,
    permission: string
  ): boolean => {
    const modulePermissions = role.permissions[module];
    return modulePermissions && permission in modulePermissions
      ? Boolean(modulePermissions[permission as keyof typeof modulePermissions])
      : false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            User Permissions Overview
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Manage user roles and their access permissions across accounting
            modules
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
        >
          Add Role
        </Button>
      </Box>

      {/* Role Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {roles.map((role) => {
          const { granted, total } = getRolePermissionCount(role);
          const percentage = Math.round((granted / total) * 100);

          return (
            <Grid item xs={12} sm={6} md={3} key={role.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  },
                }}
                onClick={() => handleOpenDialog(role)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {role.name}
                    </Typography>
                    <IconButton size="small" sx={{ color: "primary.main" }}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
                  >
                    {percentage}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 1 }}
                  >
                    {granted} of {total} permissions
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {permissionModules.map((module) => {
                      const modulePermissions = role.permissions[module.key];
                      const hasAnyPermission = modulePermissions
                        ? Object.values(modulePermissions).some(Boolean)
                        : false;
                      return (
                        <Chip
                          key={module.key}
                          size="small"
                          label={module.label}
                          color={hasAnyPermission ? "primary" : "default"}
                          variant={hasAnyPermission ? "filled" : "outlined"}
                        />
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Permissions Matrix */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Permissions Matrix
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Click on permission cells to toggle access quickly
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                    Role
                  </TableCell>
                  {permissionModules.map((module) => (
                    <TableCell
                      key={module.key}
                      align="center"
                      sx={{ fontWeight: 600 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <Icon sx={{ color: module.color, fontSize: 18 }}>
                          {module.icon}
                        </Icon>
                        {module.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {role.name}
                      </Typography>
                    </TableCell>
                    {permissionModules.map((module) => (
                      <TableCell key={module.key} align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {module.permissions.map((permission) => {
                            const roleHasPermission = hasPermission(
                              role,
                              module.key,
                              permission
                            );
                            return (
                              <Box
                                key={permission}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  cursor: "pointer",
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.action.hover,
                                      0.5
                                    ),
                                  },
                                }}
                                onClick={() =>
                                  handleQuickToggle(
                                    role.id,
                                    module.key,
                                    permission as PermissionAction<
                                      typeof module.key
                                    >
                                  )
                                }
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: "capitalize",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {permission}
                                </Typography>
                                {getPermissionIcon(roleHasPermission)}
                              </Box>
                            );
                          })}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Role Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingRole ? "Edit Role" : "Add New Role"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            sx={{ mt: 2, mb: 3 }}
            placeholder="e.g., Senior Accountant"
          />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Permissions
          </Typography>

          {permissionModules.map((module) => (
            <Card key={module.key} sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Icon sx={{ color: module.color }}>{module.icon}</Icon>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {module.label}
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {module.permissions.map((permission) => {
                    const modulePermissions = formData.permissions[module.key];
                    const isChecked =
                      modulePermissions && permission in modulePermissions
                        ? Boolean(
                            modulePermissions[
                              permission as keyof typeof modulePermissions
                            ]
                          )
                        : false;

                    return (
                      <Grid item xs={6} sm={4} md={3} key={permission}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) =>
                                handlePermissionToggle(
                                  module.key,
                                  permission as PermissionAction<
                                    typeof module.key
                                  >,
                                  e.target.checked
                                )
                              }
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {permission}
                            </Typography>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name.trim()}
          >
            {editingRole ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
